<template>
  <div>
    <nav class="navbar navbar-expand-lg navbar-dark bg-primary sticky-top mb-5">
      <div class="container-xl">
        <h4 class="navbar-brand w-25 mb-0">
          <LogoPublic />
        </h4>
      </div>
    </nav>

    <div class="container-xl text-container">
      <!-- Loading -->
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status" />
      </div>

      <!-- Not found / not bookable -->
      <div v-else-if="!event" class="alert alert-warning">
        Event not found.
      </div>

      <!-- Confirmation panel (after booking) -->
      <div v-else-if="booking" class="my-4">
        <div class="alert alert-success">
          <h4 class="alert-heading">Your booking is received</h4>
          <p>
            Booking reference: <b>{{ booking.reference }}</b>
          </p>
          <p v-if="booking.booking_status === 'confirmed'">
            Your place is confirmed.
          </p>
          <p v-else>
            Your booking is pending and will be confirmed as soon as it has been verified.
          </p>
          <p v-if="booking.price_total > 0">
            Total: {{ formatPrice(booking.price_total) }}
            {{ booking.currency }} (invoice will be sent separately)
          </p>
        </div>

        <h5>{{ event.title }}</h5>
        <p>
          {{ event.event_dates }}
          <span v-if="event.location">
            &middot; {{ event.location }}
          </span>
        </p>

        <table class="table table-sm">
          <thead>
            <tr>
              <th>Attendee</th>
              <th>Qty</th>
              <th>E-mail</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(a, i) in booking.attendees" :key="i">
              <td>{{ a.name }}</td>
              <td>{{ a.quantity }}</td>
              <td>{{ a.email }}</td>
            </tr>
          </tbody>
        </table>

        <p v-if="booking.account.exists">
          An account with e-mail
          <b>{{ registrant.email }}</b>
          already exists.
        </p>
        <div class="d-flex gap-2 flex-wrap">
          <a class="btn btn-primary" :href="loginReturnUrl">Log in to link your booking</a>
          <a v-if="!booking.account.exists" class="btn btn-outline-primary" :href="registerUrl">Create an account to link your booking</a>
          <a class="btn btn-outline-secondary" :href="config.web_url + '/'" >Back to home</a>
        </div>
      </div>

      <!-- Booking form -->
      <form v-else @submit.prevent="submitBooking()">
        <h2>{{ event.title }}</h2>
        <p class="text-muted">
          {{ event.event_dates }}
          <span v-if="event.location">
            &middot; {{ event.location }}
          </span>
        </p>
        <div
          v-if="event.description"
          class="mb-4"
          v-html="event.description"
        />

        <div v-if="!event.bookable" class="alert alert-warning">
          <span v-if="event.status === 'cancelled'">This event has been cancelled.</span>
          <span v-else-if="!event.future">This event has already taken place.</span>
          <span v-else-if="event.full">This event is fully booked.</span>
          <span v-else>Booking for this event is not open.</span>
        </div>

        <template v-if="event.bookable">
          <!-- Step 1: Your details -->
          <div class="card mb-4">
            <div class="card-header">
              1. Your details
            </div>
            <div class="card-body">
              <div class="row g-3">
                <div class="col-md-4">
                  <label class="form-label">First name <span class="text-danger">*</span></label>
                  <input v-model.trim="registrant.first_name" class="form-control" type="text" />
                </div>
                <div class="col-md-4">
                  <label class="form-label">Last name <span class="text-danger">*</span></label>
                  <input v-model.trim="registrant.last_name" class="form-control" type="text" />
                </div>
                <div class="col-md-4">
                  <label class="form-label">E-mail <span class="text-danger">*</span></label>
                  <input v-model.trim="registrant.email" class="form-control" type="email" @blur="registrantEmailCheck()" />
                  <div v-if="registrantEmailState === 1" class="form-text">
                    An account with this e-mail already exists &mdash; you can log in afterwards to link your booking.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Step 2: Seats & attendees -->
          <div class="card mb-4">
            <div class="card-header">
              2. Seats &amp; attendees
            </div>
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-start">
                <div>
                  <b>{{ event.title }}</b>
                  <div class="text-muted">
                    {{ formatPrice(event.price) }} {{ event.currency }}
                    <span v-if="event.full">
                      &mdash; sold out
                    </span>
                  </div>
                </div>
                <div class="input-group" style="max-width: 12rem;">
                  <button
                    class="btn btn-outline-secondary"
                    type="button"
                    :disabled="attendees[0].quantity <= 0"
                    @click="decSeats"
                  >
                    &minus;
                  </button>
                  <input
                    :value="attendees[0].quantity"
                    class="form-control text-center"
                    type="text"
                    readonly
                  >
                  <button
                    class="btn btn-outline-secondary"
                    type="button"
                    :disabled="attendees[0].quantity >= maxSeats()"
                    @click="incSeats"
                  >
                    +
                  </button>
                </div>
              </div>

              <template v-if="attendees[0].quantity > 0">
                  <hr>
                  <div class="row g-3">
                    <div class="col-md-4">
                      <label class="form-label">Attendee first name <span class="text-danger">*</span></label>
                      <input v-model.trim="attendees[0].first_name" class="form-control" type="text" />
                    </div>
                    <div class="col-md-4">
                      <label class="form-label">Attendee last name <span class="text-danger">*</span></label>
                      <input v-model.trim="attendees[0].last_name" class="form-control" type="text" />
                    </div>
                    <div class="col-md-4">
                      <label class="form-label">Attendee e-mail</label>
                      <input
                        v-model.trim="attendees[0].email"
                        class="form-control"
                        type="email"
                        :placeholder="registrant.email"
                      >
                    </div>
                  </div>

                  <!-- Dynamic form fields for this attendee -->
                  <div
                    v-for="(f, fi) in formFields"
                    :key="f.type + fi"
                    class="mb-3 mt-3"
                  >
                    <template v-if="f.type === 'name'">
                      <label class="form-label">{{ fieldLabel(f) }}</label>
                      <input :value="attendees[0].first_name + ' ' + attendees[0].last_name" class="form-control" type="text" readonly>
                    </template>
                    <template v-else-if="f.type === 'mec_email'">
                      <label class="form-label">{{ fieldLabel(f) }}</label>
                      <input
                        :value="attendees[0].email || registrant.email"
                        class="form-control"
                        type="email"
                        readonly
                      >
                    </template>
                    <template v-else-if="f.type === 'agreement'">
                      <div class="form-check">
                        <input
                          :id="'agreement-' + fi"
                          v-model="attendees[0].data['f' + fi]"
                          class="form-check-input"
                          type="checkbox"
                        >
                        <label :for="'agreement-' + fi" class="form-check-label" v-html="agreementHtml(f)"></label>
                      </div>
                    </template>
                    <template v-else-if="f.type === 'radio'">
                      <label class="form-label">{{ fieldLabel(f) }}</label>
                      <div
                        v-for="(o, oi) in f.options"
                        :key="oi"
                        class="form-check"
                      >
                        <input
                          :id="'radio-' + fi + '-' + oi"
                          v-model="attendees[0].data['f' + fi]"
                          class="form-check-input"
                          type="radio"
                          :name="'radio-' + fi"
                          :value="o.label"
                        >
                        <label :for="'radio-' + fi + '-' + oi" class="form-check-label">{{ o.label }}</label>
                      </div>
                    </template>
                    <template v-else-if="f.type === 'checkbox'">
                      <div class="form-check">
                        <input
                          :id="'checkbox-' + fi"
                          v-model="attendees[0].data['f' + fi]"
                          class="form-check-input"
                          type="checkbox"
                        >
                        <label :for="'checkbox-' + fi" class="form-check-label">{{ fieldLabel(f) }}</label>
                      </div>
                    </template>
                    <template v-else-if="f.type === 'textarea'">
                      <label class="form-label">{{ fieldLabel(f) }}</label>
                      <textarea v-model="attendees[0].data['f' + fi]" class="form-control" rows="3"></textarea>
                    </template>
                    <template v-else>
                      <label class="form-label">{{ fieldLabel(f) }}</label>
                      <input v-model="attendees[0].data['f' + fi]" class="form-control" type="text" />
                    </template>
                  </div>
                </template>
              </div>
            </div>

          <!-- Step 3: Confirm -->
          <div class="card mb-4">
            <div class="card-header">
              3. Confirm
            </div>
            <div class="card-body">
              <div
                v-if="error"
                class="alert alert-danger"
              >
                {{ error }}
              </div>
              <table v-if="totalSeats > 0" class="table table-sm">
                <thead>
                  <tr>
                    <th>Event</th>
                    <th>Qty</th>
                    <th>Price</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{{ event.title }}</td>
                    <td>{{ attendees[0].quantity }}</td>
                    <td>{{ formatPrice(priceTotal) }} {{ event.currency }}</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr>
                    <td colspan="2" class="text-end">
                      Total:
                    </td>
                    <td>{{ formatPrice(priceTotal) }} {{ event.currency }}</td>
                  </tr>
                </tfoot>
              </table>
              <p v-if="priceTotal > 0" class="text-muted">
                Payment: invoice will be sent separately after confirmation.
              </p>
              <button
                class="btn btn-primary"
                type="submit"
                :disabled="saving || totalSeats === 0"
              >
                <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
                Book
              </button>
            </div>
          </div>
        </template>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import { config } from "../../config";
import LogoPublic from "./LogoPublic.vue";

const store = useStore();
const route = useRoute();

const lang = computed(() => {
  const l = route.query.lang;
  return typeof l === "string" && l !== "" ? l : "en";
});

const loading = ref(true);
const event = ref(null);
const registrant = reactive({
  first_name: "",
  last_name: "",
  email: ""
});
const registrantEmailState = ref(0); // 0 = unknown, 1 = account exists
let emailTimer = null;
const attendees = reactive([]);
const formFields = ref([]);
const error = ref("");
const saving = ref(false);
const booking = ref(null);

const loginReturnUrl = computed(() => "/login?return=/event/" + route.params.id);
const registerUrl = computed(() => {
  const q = new URLSearchParams({
    email: registrant.email,
    first_name: registrant.first_name,
    last_name: registrant.last_name,
    return: "/event/" + route.params.id
  }).toString();
  return "/register?" + q;
});

function fieldLabel (f) {
  const l = f.label || {};
  return l[lang.value] || l.en || (Object.values(l)[0] || "");
}

function agreementHtml (f) {
  const l = f.label || {};
  const raw = l[lang.value] || l.en || (Object.values(l)[0] || "");
  const link = lang.value === "sv"
    ? "https://gdq.se/sv/integritetspolicy2/"
    : "https://gdq.se/en/privacypolicy/";
  return raw.replace("%s", '<a href="' + link + '" target="_blank" rel="noopener">privacy policy</a>');
}

function formatPrice (p) {
  const n = Number(p || 0);
  return n % 1 === 0 ? String(n) : n.toFixed(2);
}

const totalSeats = computed(() => attendees.reduce((s, a) => s + (a.quantity || 0), 0));

const priceTotal = computed(() => {
  if (!event.value) return 0;
  return Number(event.value.price || 0) * (attendees[0].quantity || 0);
});

function maxSeats () {
  if (!event.value) return Infinity;
  return event.value.available === null ? Infinity : event.value.available;
}

function incSeats () {
  if (attendees[0].quantity < maxSeats()) {
    attendees[0].quantity += 1;
  }
}

function decSeats () {
  if (attendees[0].quantity > 0) {
    attendees[0].quantity -= 1;
  }
}

function registrantEmailCheck () {
  if (emailTimer) clearTimeout(emailTimer);
  if (!registrant.email) {
    registrantEmailState.value = 0;
    return;
  }
  emailTimer = setTimeout(() => {
    store.dispatch("event_userexists", { email: registrant.email }).then((resp) => {
      registrantEmailState.value = resp.data.exists ? 1 : 0;
    }).catch(() => {
      registrantEmailState.value = 0;
    });
  }, 400);
}

function attendeePayload () {
  const a = attendees[0];
  if (!a || a.quantity <= 0) return [];
  return [{
    quantity: a.quantity,
    first_name: a.first_name,
    last_name: a.last_name,
    email: a.email || registrant.email,
    data: a.data
  }];
}

function validate () {
  if (!registrant.first_name || !registrant.last_name) return "Please enter your name";
  if (!registrant.email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(registrant.email)) return "Please enter a valid e-mail";
  if (totalSeats.value === 0) return "Please select at least one seat";
  const a = attendees[0];
  if (a.quantity <= 0) return "";
  if (!a.first_name || !a.last_name) return "Please enter the attendee name";
  if (a.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(a.email)) return "Invalid attendee e-mail";
  formFields.value.forEach((f, fi) => {
    const key = "f" + fi;
    if (f.type === "name" || f.type === "mec_email") return;
    const v = a.data[key];
    const empty = v === undefined || v === null || (typeof v === "string" && v.trim() === "") || (Array.isArray(v) && v.length === 0);
    if (f.mandatory && empty) {
      const lbl = fieldLabel(f) || "field";
      return "Required: " + lbl;
    }
  });
  return "";
}

function submitBooking () {
  error.value = "";
  const v = validate();
  if (v) {
    error.value = v;
    return;
  }
  saving.value = true;
  store.dispatch("event_booking_create", {
    event_id: Number(route.params.id),
    lang: lang.value,
    registrant_first_name: registrant.first_name,
    registrant_last_name: registrant.last_name,
    registrant_email: registrant.email,
    attendees: attendeePayload()
  }).then((resp) => {
    saving.value = false;
    if (resp.data.status === "ok") {
      booking.value = resp.data;
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      error.value = resp.data.error || "Booking failed";
    }
  }).catch((err) => {
    saving.value = false;
    const d = err.response ? err.response.data : null;
    error.value = (d && d.error) ? d.error : "Booking failed";
  });
}

onMounted(() => {
  store.dispatch("event_get", { id: Number(route.params.id), lang: lang.value }).then((resp) => {
    loading.value = false;
    if (resp.data.status !== "ok") return;
    event.value = resp.data.result;
    const ff = event.value.form_fields;
    formFields.value = (ff && ff.fields) ? ff.fields : [];
    attendees.length = 0;
    attendees.push({
      quantity: 0,
      first_name: "",
      last_name: "",
      email: "",
      data: {}
    });
  }).catch(() => {
    loading.value = false;
  });
});
</script>
